import { useEffect, useId, useRef, useState } from "react";
import CloseIcon from "@/assets/svgs/close.svg";
import FacebookIcon from "@/assets/svgs/socials/social-facebook.svg";
import InstagramIcon from "@/assets/svgs/socials/social-instagram.svg";
import ShareLinkIcon from "@/assets/svgs/socials/social-share-chain.svg";
import TikTokIcon from "@/assets/svgs/socials/social-tiktok.svg";
import YouTubeIcon from "@/assets/svgs/socials/social-youtube.svg";
import type { StyledComponentBase } from "@/components/component-base.types";
import { IconButton } from "@/components/shared/inputs/icon-button/icon-button";
import { Link } from "@/components/shared/inputs/link/link";
import styles from "./work-social-links.module.scss";

const INSTAGRAM_URL = "https://www.instagram.com/thriftbooks/";
const TIKTOK_URL = "https://www.tiktok.com/@thriftbooks?lang=en";
const YOUTUBE_URL = "https://www.youtube.com/c/Thriftbooks";
const COPY_CONFIRMATION_DURATION_MS = 2000;

export const copyShareUrl = async (shareUrl: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    }
  } catch {
    // Fall through to the selection-based copy method when clipboard access is denied.
  }

  const fallbackInput = document.createElement("textarea");
  fallbackInput.value = shareUrl;
  fallbackInput.setAttribute("readonly", "");
  fallbackInput.style.position = "fixed";
  fallbackInput.style.opacity = "0";
  document.body.appendChild(fallbackInput);
  fallbackInput.select();

  try {
    return (
      typeof document.execCommand === "function" && document.execCommand("copy")
    );
  } catch {
    return false;
  } finally {
    fallbackInput.remove();
  }
};

export const getCanonicalWorkUrl = (
  cleanUrl: string,
  idWork: number,
  isbn?: string | null,
) => {
  const workUrl = `https://www.thriftbooks.com/w/${cleanUrl}/${idWork}/`;
  return isbn ? `${workUrl}?isbn=${encodeURIComponent(isbn)}` : workUrl;
};

interface WorkSocialLinksProps extends StyledComponentBase {
  cleanUrl: string;
  idWork: number;
  isbn?: string | null;
}

const WorkSocialLinks = ({
  cleanUrl,
  idWork,
  isbn,
  className,
}: WorkSocialLinksProps) => {
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRootRef = useRef<HTMLElement>(null);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const sharePanelRef = useRef<HTMLDivElement>(null);
  const sharePanelId = useId();
  const workUrl = getCanonicalWorkUrl(cleanUrl, idWork, isbn);
  const facebookUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(workUrl)}`;
  const links = [
    {
      href: facebookUrl,
      label: "Share on Facebook",
      icon: FacebookIcon,
      brandClassName: styles.facebook,
    },
    {
      href: INSTAGRAM_URL,
      label: "Visit ThriftBooks on Instagram",
      icon: InstagramIcon,
      brandClassName: styles.instagram,
    },
    {
      href: TIKTOK_URL,
      label: "Visit ThriftBooks on TikTok",
      icon: TikTokIcon,
      brandClassName: styles.tiktok,
    },
    {
      href: YOUTUBE_URL,
      label: "Visit ThriftBooks on YouTube",
      icon: YouTubeIcon,
      brandClassName: styles.youtube,
    },
  ];

  const copyLink = async () => {
    setCopied(await copyShareUrl(workUrl));
  };

  const closeShare = (restoreFocus = false) => {
    setIsShareOpen(false);
    setCopied(false);
    if (restoreFocus) shareButtonRef.current?.focus();
  };

  const toggleShare = () => {
    if (isShareOpen) {
      closeShare();
      return;
    }

    setIsShareOpen(true);
    void copyLink();
  };

  useEffect(() => {
    if (!isShareOpen) return undefined;

    sharePanelRef.current?.focus();

    const handleOutsideClick = (event: MouseEvent) => {
      if (!shareRootRef.current?.contains(event.target as Node)) closeShare();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeShare(true);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isShareOpen]);

  useEffect(() => {
    if (!copied) return undefined;

    const timeout = window.setTimeout(
      () => setCopied(false),
      COPY_CONFIRMATION_DURATION_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [copied]);

  return (
    <nav
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      aria-label="Share this book"
      ref={shareRootRef}
    >
      {links.map(({ href, label, icon: Icon, brandClassName }) => (
        <Link
          key={label}
          href={href}
          linkType="external"
          target="_blank"
          rel="noopener noreferrer"
          ariaLabel={label}
          className={`${styles.link} ${brandClassName}`}
        >
          <Icon aria-hidden />
        </Link>
      ))}
      <button
        type="button"
        className={`${styles.link} ${styles.share}`}
        aria-label="Share a link to this book"
        aria-expanded={isShareOpen}
        aria-controls={isShareOpen ? sharePanelId : undefined}
        onClick={toggleShare}
        ref={shareButtonRef}
      >
        <ShareLinkIcon aria-hidden />
      </button>
      {isShareOpen && (
        <div
          id={sharePanelId}
          className={styles.sharePanel}
          role="dialog"
          aria-label="Share link"
          tabIndex={-1}
          ref={sharePanelRef}
        >
          <div className={styles.shareHeader}>
            <strong>Share this book</strong>
            <IconButton
              ariaLabel="Close share panel"
              className={styles.closeButton}
              onClick={() => closeShare(true)}
            >
              <CloseIcon aria-hidden />
            </IconButton>
          </div>
          <p className={styles.shareInstructions}>
            Copy this link to share this book.
          </p>
          <div className={styles.copyRow}>
            <input
              className={styles.shareUrl}
              aria-label="Share URL"
              value={workUrl}
              readOnly
              onClick={(event) => event.currentTarget.select()}
              onFocus={(event) => event.currentTarget.select()}
            />
            <button
              type="button"
              className={styles.copyButton}
              onClick={() => void copyLink()}
            >
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
          <span className={styles.copyStatus} aria-live="polite">
            {copied ? "Link copied to clipboard." : ""}
          </span>
        </div>
      )}
    </nav>
  );
};

export default WorkSocialLinks;
